import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageNavigation } from './usePageNavigation';
import { useZoomControls } from './useZoomControls';
import { usePdfViewer } from './usePdfViewer';
import { usePdfHighlight } from './usePdfHighlight';
import { useFeedback } from './useFeedback';
import { useInsuranceHikiukeSession } from './useInsuranceHikiukeSession';
import { useJumpButtonGroup } from './useJumpButtonGroup';
import { usePdfKeywordSearch } from '../../usePdfKeywordSearch';

/**
 * 引受目安詳細画面のメインカスタムフック
 * 
 * 各サブフックを統合してオーケストレーションを行う。
 * このフックは以下の責務を持つ:
 * - 各サブフックの初期化と連携
 * - PDF読み込みのトリガー管理
 * - react-pdfコールバックのラッピング
 * - 画面遷移とリトライ処理
 * 
 * @returns {Object} 引受目安詳細画面に必要な状態とハンドラ
 * 
 * @returns {boolean} returns.loading - PDF読み込み中かどうか
 * @returns {number} returns.zoomLevel - 現在のズームレベル（%）
 * @returns {number} returns.currentPage - 現在のページ番号
 * @returns {number} returns.totalPages - 総ページ数
 * @returns {boolean} returns.showErrorPage - エラーページ表示フラグ
 * @returns {ArrayBuffer|null} returns.pdfFile - PDFファイルデータ
 * @returns {boolean} returns.isFeedbackSubmitting - フィードバック送信中フラグ
 * @returns {string} returns.errorMessage - エラーメッセージ
 * @returns {boolean} returns.isLandscape - ページ横向きフラグ
 * @returns {boolean} returns.isRendering - レンダリング中フラグ
 * @returns {Object} returns.documentInfo - ドキュメント情報
 * @returns {boolean} returns.isDocInfoAccordionOpen - ドキュメント情報アコーディオン開閉状態
 * @returns {boolean} returns.isMobile - モバイル表示かどうか
 * @returns {string|null} returns.currentPopup - 現在表示中のポップアップ
 * @returns {string|null} returns.currentQueryId - 現在のクエリID
 * @returns {string} returns.activeJumpButton - アクティブなジャンプボタン
 * @returns {boolean} returns.canJumpDisease - 該当傷病へジャンプ可能か
 * @returns {boolean} returns.canJumpContents - 目次へジャンプ可能か
 * @returns {boolean} returns.canJumpLegend - 凡例へジャンプ可能か
 * @returns {boolean} returns.canJumpAppendix - 別表へジャンプ可能か
 * @returns {Array|null} returns.highlightsPolygon - ハイライトポリゴン座標
 * @returns {Object} returns.pdfOptions - react-pdf用設定オブジェクト
 * @returns {React.RefObject} returns.highlightCanvasRef - ハイライトCanvas要素のref
 * @returns {React.RefObject} returns.pdfContainerRef - PDFコンテナ要素のref
 * @returns {Function} returns.handleZoomIn - ズームインハンドラ
 * @returns {Function} returns.handleZoomOut - ズームアウトハンドラ
 * @returns {Function} returns.handlePageChange - ページ変更ハンドラ
 * @returns {Function} returns.handlePrevPage - 前ページへ移動ハンドラ
 * @returns {Function} returns.handleNextPage - 次ページへ移動ハンドラ
 * @returns {Function} returns.handleChangePageInput - ページ入力変更ハンドラ
 * @returns {Function} returns.handlePageInputBlur - ページ入力フォーカスアウトハンドラ
 * @returns {Function} returns.handleRetry - リトライハンドラ
 * @returns {Function} returns.handleBackToSearch - 検索画面に戻るハンドラ
 * @returns {Function} returns.toggleDocInfoAccordion - ドキュメント情報アコーディオン切替ハンドラ
 * @returns {Function} returns.handleClose - ウィンドウを閉じるハンドラ
 * @returns {Function} returns.handleFeedbackClose - フィードバックポップアップを閉じるハンドラ
 * @returns {Function} returns.handleGoodFeedback - 良いフィードバックハンドラ
 * @returns {Function} returns.handleBadFeedback - 悪いフィードバックハンドラ
 * @returns {Function} returns.handleFeedbackSubmit - フィードバック送信ハンドラ
 * @returns {Function} returns.handleFeedbackBack - フィードバック戻るハンドラ
 * @returns {Function} returns.handleJumpDisease - 該当傷病へジャンプハンドラ
 * @returns {Function} returns.handleJumpContents - 目次へジャンプハンドラ
 * @returns {Function} returns.handleJumpLegends - 凡例へジャンプハンドラ
 * @returns {Function} returns.handleJumpAppendix - 別表へジャンプハンドラ
 * @returns {Function} returns.onDocumentLoadSuccess - ドキュメント読み込み成功コールバック
 * @returns {Function} returns.onDocumentLoadError - ドキュメント読み込みエラーコールバック
 * @returns {Function} returns.onPageLoadSuccess - ページ読み込み成功コールバック
 * @returns {Function} returns.onPageLoadError - ページ読み込みエラーコールバック
 * @returns {Function} returns.setCurrentPage - 現在ページ設定関数
 */
export const useInsuranceHikiukeDetail = () => {
  const navigate = useNavigate();

  /**
   * サブフック: ズームコントロール
   * ズームイン・ズームアウト操作を提供
   */
  const zoomControls = useZoomControls();
  const {
    zoomLevel,
    handleZoomIn,
    handleZoomOut,
  } = zoomControls;

  /**
   * サブフック: ページナビゲーション
   * ページ移動、ページ入力を提供
   */
  const navigation = usePageNavigation();
  const {
    currentPage,
    totalPages,
    isRendering,
    setCurrentPage,
    setTotalPages,
    setIsRendering,
    handlePageChange,
    handlePrevPage,
    handleNextPage,
    handleChangePageInput,
    handlePageInputBlur,
    jumpToPage,
    isJumpPageValid,
  } = navigation;

  /**
   * サブフック: PDFビューワー
   * PDF読み込み、表示、エラー処理を提供
   */
  const pdfViewer = usePdfViewer({
    setIsRendering,
  });
  const {
    loading,
    pdfFile,
    pdfDocument,
    showErrorPage,
    errorMessage,
    isLandscape,
    pageInfo,
    pdfOptions,
    loadPdfDocument,
    onDocumentLoadSuccess: pdfDocumentLoadSuccess,
    onDocumentLoadError,
    onPageLoadSuccess: pdfPageLoadSuccess,
    onPageLoadError: pdfPageLoadError,
    resetPdfState,
    handleError,
  } = pdfViewer;

  /**
   * サブフック: PDFハイライト
   * 検索結果箇所のハイライト表示を提供
   */
  const highlight = usePdfHighlight({
    pageInfo,
    currentPage,
    zoomLevel,
  });
  const {
    highlightsPolygon,
    highlightCanvasRef,
    pdfContainerRef,
    initializeHighlight,
  } = highlight;

  /**
   * サブフック: セッション管理
   * セッションストレージからのデータ復元を提供
   */
  const session = useInsuranceHikiukeSession({
    onError: handleError,
    setCurrentPage,
    initializeHighlight,
  });
  const {
    detailResult,
    blobName,
    currentQueryId,
    documentInfo,
    isMobile,
    isDocInfoAccordionOpen,
    toggleDocInfoAccordion,
  } = session;

  /**
   * サブフック: フィードバック
   * ユーザーフィードバック機能を提供
   */
  const feedback = useFeedback(currentQueryId);
  const {
    currentPopup,
    isFeedbackSubmitting,
    handleFeedbackClose,
    handleGoodFeedback,
    handleBadFeedback,
    handleFeedbackSubmit,
    handleFeedbackBack,
  } = feedback;

  /**
   * サブフック: ジャンプボタングループ
   * 該当傷病、目次、凡例、別表へのジャンプを提供
   */
  const jumpButtonGroup = useJumpButtonGroup({
    detailResult,
    jumpToPage,
    isJumpPageValid,
  });
  const {
    activeJumpButton,
    canJumpDisease,
    canJumpContents,
    canJumpLegend,
    canJumpAppendix,
    handleJumpDisease,
    handleJumpContents,
    handleJumpLegends,
    handleJumpAppendix,
  } = jumpButtonGroup;

  // モバイル用検索パネルの開閉状態（UI状態のため検索ロジックフックには持たせない）
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  /**
   * サブフック: PDFキーワード検索
   * テキスト検索・ページナビゲーション・ハイライトを提供
   */
  const keywordSearch = usePdfKeywordSearch({
    pdfDocument,
    currentPage,
    setCurrentPage,
    containerRef: pdfContainerRef,
  });
  const {
    searchText,
    setSearchText,
    searchResults,
    currentSearchIndex,
    isSearching,
    searchError,
    handleSearch,
    handleNextSearchResult,
    handlePrevSearchResult,
    handleClearSearch,
    customTextRenderer,
    onGetTextSuccess,
    onTextLayerRenderSuccess,
  } = keywordSearch;

  /**
   * Effect: PDF読み込みトリガー
   * blobNameとdetailResultが揃ったらPDFを読み込む
   */
  useEffect(() => {
    if (blobName && detailResult) {
      loadPdfDocument(blobName, detailResult);
    }
  }, [blobName, detailResult, loadPdfDocument]);

  /**
   * react-pdfコールバックラッパー
   * サブフックのコールバックをラップして追加処理を行う
   */

  /**
   * ドキュメント読み込み成功コールバック
   * 総ページ数を設定する
   */
  const onDocumentLoadSuccess = useCallback((result) => {
    pdfDocumentLoadSuccess(result, setTotalPages);
  }, [pdfDocumentLoadSuccess, setTotalPages]);

  /**
   * ページ読み込み成功コールバック
   */
  const onPageLoadSuccess = useCallback((page) => {
    pdfPageLoadSuccess(page);
  }, [pdfPageLoadSuccess]);

  /**
   * ページ読み込みエラーコールバック
   * 現在のページ番号をエラーメッセージに含める
   */
  const onPageLoadError = useCallback((error) => {
    pdfPageLoadError(error, currentPage);
  }, [pdfPageLoadError, currentPage]);

  /**
   * PDF読み込みをリトライする
   * 状態をリセットしてから再読み込みを実行
   */
  const handleRetry = useCallback(() => {
    resetPdfState();
    setTotalPages(0);
    setCurrentPage(1);
    setIsRendering(false);
    setTimeout(() => {
      loadPdfDocument(blobName, detailResult);
    }, 100);
  }, [resetPdfState, setTotalPages, setCurrentPage, setIsRendering, loadPdfDocument, blobName, detailResult]);

  /**
   * 検索画面に戻る
   */
  const handleBackToSearch = useCallback(() => {
    navigate('/solicitor/insurance/system/search');
  }, [navigate]);

  /**
   * ウィンドウを閉じる
   * 新しいタブで開かれた場合に使用
   */
  const handleClose = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.close();
    }
  }, []);

  /**
   * 戻り値
   */
  return {
    // 状態: ローディング・エラー
    loading,
    showErrorPage,
    errorMessage,
    isRendering,

    // 状態: PDF表示
    pdfFile,
    pdfOptions,
    isLandscape,
    zoomLevel,
    currentPage,
    totalPages,

    // 状態: ハイライト
    highlightsPolygon,

    // 状態: ドキュメント情報
    documentInfo,
    isDocInfoAccordionOpen,

    // 状態: フィードバック
    currentPopup,
    isFeedbackSubmitting,
    currentQueryId,

    // 状態: ジャンプボタン
    activeJumpButton,
    canJumpDisease,
    canJumpContents,
    canJumpLegend,
    canJumpAppendix,

    // 状態: レスポンシブ
    isMobile,

    // refs
    highlightCanvasRef,
    pdfContainerRef,

    // ハンドラ: ズーム
    handleZoomIn,
    handleZoomOut,

    // ハンドラ: ページ移動
    handlePageChange,
    handlePrevPage,
    handleNextPage,
    handleChangePageInput,
    handlePageInputBlur,
    setCurrentPage,

    // ハンドラ: ジャンプ
    handleJumpDisease,
    handleJumpContents,
    handleJumpLegends,
    handleJumpAppendix,

    // ハンドラ: フィードバック
    handleFeedbackClose,
    handleGoodFeedback,
    handleBadFeedback,
    handleFeedbackSubmit,
    handleFeedbackBack,

    // ハンドラ: 画面操作
    handleRetry,
    handleBackToSearch,
    toggleDocInfoAccordion,
    handleClose,

    // react-pdfコールバック
    onDocumentLoadSuccess,
    onDocumentLoadError,
    onPageLoadSuccess,
    onPageLoadError,

    // キーワード検索
    searchText,
    setSearchText,
    searchResults,
    currentSearchIndex,
    isSearching,
    searchError,
    isSearchOpen,
    setIsSearchOpen,
    handleSearch,
    handleNextSearchResult,
    handlePrevSearchResult,
    handleClearSearch,
    customTextRenderer,
    onGetTextSuccess,
    onTextLayerRenderSuccess,
    pdfDocument,
  };
};

export default useInsuranceHikiukeDetail;
